import { useEffect, useState } from "react";
import { Check, X, ShieldCheck } from "lucide-react";
import { api, brl, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const COLOR = { pendente: "#F5C518", aprovado: "#2E8B57", recusado: "#E53238", none: "#9CA3AF" };

export default function AdminWithdrawals() {
  const [rows, setRows] = useState([]);
  const [kyc, setKyc] = useState([]);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/admin/withdrawals"), api.get("/admin/kyc")]);
    setRows(a.data);
    setKyc(b.data);
  };
  useEffect(() => { load(); }, []);

  const act = async (url, msg) => {
    try { await api.post(url); toast.success(msg); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-withdrawals">
      <h1 className="font-display text-3xl font-extrabold">Saques e KYC</h1>
      <p className="text-sm text-gray-500">Aprovação da administradora Time Food</p>

      <h2 className="font-display font-bold text-lg mt-6">Solicitações de saque</h2>
      <div className="rounded-2xl border border-gray-200 bg-white mt-2 overflow-x-auto">
        <Table data-testid="withdrawals-table">
          <TableHeader>
            <TableRow>
              <TableHead>Usuário</TableHead>
              <TableHead>Papel</TableHead>
              <TableHead>Chave Pix</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead className="text-right">Taxa</TableHead>
              <TableHead className="text-right">Líquido</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`withdrawal-row-${r.id}`}>
                <TableCell className="font-semibold">{r.user_name}</TableCell>
                <TableCell className="text-xs text-gray-600">{r.user_role}</TableCell>
                <TableCell className="text-xs text-gray-600">{r.pix_key}</TableCell>
                <TableCell className="text-right font-display font-extrabold">{brl(r.amount)}</TableCell>
                <TableCell className="text-right text-xs">{r.fee_pct}% ({brl(r.fee)})</TableCell>
                <TableCell className="text-right font-semibold text-mage-green">{brl(r.net_amount)}</TableCell>
                <TableCell>
                  <span className="text-[10px] font-bold px-2 py-1 rounded-full text-white" style={{ background: COLOR[r.status] }}>
                    {r.status.toUpperCase()}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  {r.status === "pendente" && (
                    <div className="flex gap-1 justify-end">
                      <Button data-testid={`approve-withdrawal-${r.id}`} size="sm"
                        onClick={() => act(`/admin/withdrawals/${r.id}/aprovar`, "Saque aprovado")}
                        className="h-8 rounded-lg bg-mage-green text-white font-bold">
                        <Check size={14} />
                      </Button>
                      <Button data-testid={`reject-withdrawal-${r.id}`} size="sm" variant="outline"
                        onClick={() => act(`/admin/withdrawals/${r.id}/recusar`, "Saque recusado")}
                        className="h-8 rounded-lg text-mage-red border-mage-red">
                        <X size={14} />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-sm text-gray-500 py-6">
                Nenhuma solicitação de saque.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <h2 className="font-display font-bold text-lg mt-8 flex items-center gap-2">
        <ShieldCheck size={18} className="text-gf-orange" /> Verificações KYC
      </h2>
      <div className="rounded-2xl border border-gray-200 bg-white mt-2 overflow-x-auto">
        <Table data-testid="kyc-table">
          <TableHeader>
            <TableRow>
              <TableHead>Usuário</TableHead>
              <TableHead>CPF</TableHead>
              <TableHead>Pix</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {kyc.map((u) => (
              <TableRow key={u.id} data-testid={`kyc-row-${u.email}`}>
                <TableCell className="font-semibold">{u.kyc?.full_name || u.name}<br />
                  <span className="text-[11px] text-gray-500">{u.email}</span></TableCell>
                <TableCell className="text-xs">{u.kyc?.cpf}</TableCell>
                <TableCell className="text-xs">{u.kyc?.pix_type}: {u.kyc?.pix_key}</TableCell>
                <TableCell>
                  <span className="text-[10px] font-bold px-2 py-1 rounded-full text-white" style={{ background: COLOR[u.kyc?.status] || "#9CA3AF" }}>
                    {(u.kyc?.status || "none").toUpperCase()}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  {u.kyc?.status === "pendente" && (
                    <div className="flex gap-1 justify-end">
                      <Button data-testid={`approve-kyc-${u.email}`} size="sm"
                        onClick={() => act(`/admin/kyc/${u.id}/aprovar`, "KYC aprovado")}
                        className="h-8 rounded-lg bg-mage-green text-white font-bold"><Check size={14} /></Button>
                      <Button data-testid={`reject-kyc-${u.email}`} size="sm" variant="outline"
                        onClick={() => act(`/admin/kyc/${u.id}/recusar`, "KYC recusado")}
                        className="h-8 rounded-lg text-mage-red border-mage-red"><X size={14} /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {kyc.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-gray-500 py-6">
                Nenhum KYC enviado.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
